// frontend/src/pages/ListadoClientes.js
import { useState, useEffect } from 'react';
import { getClientesActivos, eliminarCliente, getClientesInactivos } from '../../services/clienteService';
import { getTipoTramites } from '../../services/tipoTramiteService';
import { useNavigate } from 'react-router-dom';
import ModalRegistrarCliente from '../../components/ModalRegistrarCliente';
import PreviewCancelacionBajaCliente from '../../components/PreviewCancelacionBajaCliente';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Snackbar,
    Alert,
    Pagination
} from '@mui/material';
import PageHeader from '../../components/ui/PageHeader';
import { apellidoNombre } from '../../utils/nombrePersona';
import ChipsTramitesCliente from '../../components/ChipsTramitesCliente';
import useBreakpoint from '../../hooks/useBreakpoint';
import ListaTarjetas from '../../components/mobile/ListaTarjetas';
import MenuAcciones from '../../components/mobile/MenuAcciones';
import BottomSheet from '../../components/mobile/BottomSheet';
import BotonFiltros from '../../components/mobile/BotonFiltros';
import ChipsFiltros from '../../components/mobile/ChipsFiltros';
import TarjetaKpi from '../../components/ui/TarjetaKpi';
import { CoincidenciasPlanilla } from '../../components/PlanillaHistorica/PlanillaHistorica';

function ListadoClientes() {
    const [clientes, setClientes] = useState([]);
    const [totalClientes, setTotalClientes] = useState(0);
    const [totalInactivos, setTotalInactivos] = useState(0);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [filtroCuil, setFiltroCuil] = useState('');
    const [filtroNombreApellido, setFiltroNombreApellido] = useState('');
    const [filtroTipoTramite, setFiltroTipoTramite] = useState('');
    const [filtroReferencia, setFiltroReferencia] = useState('');
    const [tiposTramite, setTiposTramite] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [dialogoEliminar, setDialogoEliminar] = useState({ open: false, cuil: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [observacionEliminar, setObservacionEliminar] = useState('');
    const [previewBajaCargado, setPreviewBajaCargado] = useState(false);
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    const navigate = useNavigate();

    const cargarClientes = async () => {
        setLoading(true);
        try {
            const data = await getClientesActivos(page, 15, filtroCuil, filtroNombreApellido, filtroTipoTramite || null, filtroReferencia);
            setClientes(data.content || []);
            setTotalPages(data.totalPages || 0);
            setTotalClientes(data.totalElements || 0);
        } catch (e) {
            setSnackbar({ open: true, message: 'Error al cargar clientes', severity: 'error' });
        }
        setLoading(false);
    };

    const cargarClientesInactivos = async () => {
        try {
            const data = await getClientesInactivos(0, 1);
            setTotalInactivos(data.totalElements || 0);
        } catch (e) {
            setSnackbar({ open: true, message: 'Error al cargar clientes inactivos', severity: 'error' });
        }
    };

    const formatearCuil = (cuil) => {
        if (!cuil) return '';
        const cuilStr = cuil.toString();
        if (cuilStr.length !== 11) return cuilStr;
        return `${cuilStr.slice(0, 2)}-${cuilStr.slice(2, 10)}-${cuilStr.slice(10)}`;
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return '';
        const partes = fecha.split('-');
        if (partes.length !== 3) return fecha;
        return `${partes[2]}-${partes[1]}-${partes[0]}`;
    };

    useEffect(() => {
        cargarClientesInactivos();
        getTipoTramites().then(setTiposTramite).catch(() => {});
    }, []);

    useEffect(() => {
        cargarClientes();
    }, [page, filtroCuil, filtroNombreApellido, filtroTipoTramite, filtroReferencia]);

    const handleOpenModal = () => setOpenModal(true);
    const handleCloseAndReloadModal = () => {
        setOpenModal(false);
        setPage(0);
        cargarClientes();
        cargarClientesInactivos();
    };
    const handleSuccess = () => {
        setSnackbar({ open: true, message: 'Cliente registrado con éxito', severity: 'success' });
        setPage(0);
        cargarClientes();
        cargarClientesInactivos();
    };

    const verDetalles = (cuil) => navigate(`/clientes/vista-detallada/${cuil.toString()}`);
    const abrirDialogoEliminar = (cuil) => {
        setDialogoEliminar({ open: true, cuil });
        const cliente = clientes.find(c => c.cuil === cuil);
        setObservacionEliminar(cliente?.observacion || '');
        setPreviewBajaCargado(false);
    };

    const cerrarDialogoEliminar = () => setDialogoEliminar({ open: false, cuil: null });

    const confirmarEliminacion = async () => {
        if (dialogoEliminar.cuil) {
            try {
                await eliminarCliente(dialogoEliminar.cuil, observacionEliminar); // <-- enviamos observación
                setSnackbar({ open: true, message: 'Cliente dado de baja correctamente', severity: 'success' });
                cerrarDialogoEliminar();
                setPage(0);
                cargarClientes();
                cargarClientesInactivos();
            } catch (error) {
                setSnackbar({ open: true, message: 'Error al eliminar el cliente', severity: 'error' });
            }
        }
    };

    // Reemplaza el return completo en ListadoClientes.js por:
    return (
        <div className="area-general">
            <div className="container-fluid mt-4">
                {/* Header Moderno */}
                <div className="mb-4">
                    {esMobile ? (
                        <div className="mob-toolbar-pantalla">
                            <div className="mob-toolbar-titulo">
                                <h2><i className="bi bi-people-fill me-2"></i>Clientes</h2>
                                <p>Gestión de clientes del estudio</p>
                            </div>
                            <div className="mob-toolbar-acciones">
                                <button
                                    type="button"
                                    className="mob-toolbar-boton-primario mob-tocable"
                                    onClick={handleOpenModal}
                                >
                                    <i className="bi bi-person-plus-fill" aria-hidden="true" />
                                    Registrar
                                </button>
                                <MenuAcciones
                                    acciones={[
                                        { label: 'Importar CSV', icon: 'bi bi-upload', onClick: () => navigate('/clientes/importar-anses') },
                                        { label: 'Ver Inactivos', icon: 'bi bi-eye-slash', onClick: () => navigate('/clientes/inactivos') },
                                    ]}
                                />
                            </div>
                        </div>
                    ) : (
                        <PageHeader
                            variante="plano"
                            icono="people-fill"
                            titulo="Clientes"
                            descripcion="Gestión de clientes del estudio jurídico"
                            accion={
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-outline-primary d-flex align-items-center gap-2"
                                        onClick={() => navigate('/clientes/importar-anses')}
                                        style={{ borderRadius: '8px', padding: '0.6rem 1.2rem', fontWeight: '500' }}
                                        title="Importar clientes desde el CSV de turnos ANSES"
                                    >
                                        <i className="bi bi-upload" />
                                        Importar CSV
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary d-flex align-items-center gap-2"
                                        onClick={() => navigate('/clientes/inactivos')}
                                        style={{ borderRadius: '8px', padding: '0.6rem 1.2rem', fontWeight: '500' }}
                                    >
                                        <i className="bi bi-eye-slash me-1" />
                                        Ver Inactivos
                                    </button>
                                    <button
                                        className="btn btn-primary d-flex align-items-center gap-2"
                                        onClick={handleOpenModal}
                                        style={{ borderRadius: '8px', padding: '0.6rem 1.5rem', fontWeight: '500' }}
                                    >
                                        <i className="bi bi-person-plus-fill me-1" />
                                        Registrar Cliente
                                    </button>
                                </div>
                            }
                        />
                    )}

                    {/* Estadísticas Cards */}
                    <div className="row g-3 mb-4 mob-grid-2col">
                        <div className="col-md-6">
                            <TarjetaKpi label="Clientes activos" valor={totalClientes} icono="person-check-fill" />
                        </div>
                        <div className="col-md-6">
                            <TarjetaKpi tono="neutro" label="Clientes inactivos" valor={totalInactivos} icono="person-lines-fill" />
                        </div>
                    </div>
                </div>

                {/* Filtros Modernos */}
                {(() => {
                    const campoCuil = (
                        <>
                            <label className="form-label" >
                                <i className="bi bi-search me-2"></i>Buscar por CUIL
                            </label>
                            <div className="input-group">
                                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                                    <i className="bi bi-card-text" style={{ color: '#6B7280' }}></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Ej: 20-12345678-9"
                                    value={filtroCuil}
                                    onChange={(e) => {
                                        setPage(0);
                                        setFiltroCuil(e.target.value);
                                    }}
                                    style={{ borderLeft: 'none', fontSize: '1rem' }}
                                />
                            </div>
                        </>
                    );
                    const campoNombre = (
                        <>
                            <label className="form-label" >
                                <i className="bi bi-person-search me-2"></i>Buscar por Nombre o Apellido
                            </label>
                            <div className="input-group">
                                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                                    <i className="bi bi-person" style={{ color: '#6B7280' }}></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Ingrese nombre o apellido..."
                                    value={filtroNombreApellido}
                                    onChange={(e) => {
                                        setPage(0);
                                        setFiltroNombreApellido(e.target.value);
                                    }}
                                    style={{ borderLeft: 'none', fontSize: '1rem' }}
                                />
                            </div>
                        </>
                    );
                    const campoTipo = (
                        <>
                            <label className="form-label" >
                                <i className="bi bi-folder me-2"></i>Filtrar por Tipo de Trámite
                            </label>
                            <select
                                className="form-select"
                                value={filtroTipoTramite}
                                onChange={(e) => {
                                    setPage(0);
                                    setFiltroTipoTramite(e.target.value);
                                }}
                                style={{ fontSize: '1rem' }}
                            >
                                <option value="">Todos los tipos</option>
                                {tiposTramite.map((tipo) => (
                                    <option key={tipo.idTipoTramite} value={tipo.idTipoTramite}>
                                        {tipo.nombre}
                                    </option>
                                ))}
                            </select>
                        </>
                    );
                    const campoReferencia = (
                        <>
                            <label className="form-label" >
                                <i className="bi bi-bookmark me-2"></i>Buscar por Referencia
                            </label>
                            <div className="input-group">
                                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                                    <i className="bi bi-search" style={{ color: '#6B7280' }}></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Ej: Facebook, Empleado..."
                                    value={filtroReferencia}
                                    onChange={(e) => {
                                        setPage(0);
                                        setFiltroReferencia(e.target.value);
                                    }}
                                    style={{ borderLeft: 'none', fontSize: '1rem' }}
                                />
                            </div>
                        </>
                    );

                    const limpiarFiltros = () => {
                        setPage(0);
                        setFiltroCuil('');
                        setFiltroNombreApellido('');
                        setFiltroTipoTramite('');
                        setFiltroReferencia('');
                    };

                    const chipsFiltros = [
                        filtroCuil && { key: 'cuil', label: `CUIL: ${filtroCuil}`, onQuitar: () => { setPage(0); setFiltroCuil(''); } },
                        filtroNombreApellido && { key: 'nombre', label: filtroNombreApellido, onQuitar: () => { setPage(0); setFiltroNombreApellido(''); } },
                        filtroTipoTramite && {
                            key: 'tipo',
                            label: tiposTramite.find((t) => String(t.idTipoTramite) === String(filtroTipoTramite))?.nombre || 'Tipo',
                            onQuitar: () => { setPage(0); setFiltroTipoTramite(''); },
                        },
                        filtroReferencia && { key: 'referencia', label: filtroReferencia, onQuitar: () => { setPage(0); setFiltroReferencia(''); } },
                    ].filter(Boolean);

                    return esMobile ? (
                        <div className="mb-4">
                            <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                            <ChipsFiltros chips={chipsFiltros} />
                            {sheetFiltrosAbierto && (
                                <BottomSheet
                                    titulo="Filtros"
                                    onCerrar={() => setSheetFiltrosAbierto(false)}
                                    accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar filtros', onClick: limpiarFiltros } : undefined}
                                >
                                    <div className="mb-3">{campoCuil}</div>
                                    <div className="mb-3">{campoNombre}</div>
                                    <div className="mb-3">{campoTipo}</div>
                                    <div>{campoReferencia}</div>
                                </BottomSheet>
                            )}
                        </div>
                    ) : (
                        <div className="card shadow-sm mb-4" style={{ border: 'none', borderRadius: '12px' }}>
                            <div className="card-body" style={{ padding: '1.5rem' }}>
                                <div className="row g-3">
                                    <div className="col-md-3">{campoCuil}</div>
                                    <div className="col-md-3">{campoNombre}</div>
                                    <div className="col-md-3">{campoTipo}</div>
                                    <div className="col-md-3">{campoReferencia}</div>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Tabla Moderna (desktop) / Tarjetas (mobile) */}
                {esMobile ? (
                    <>
                        <ListaTarjetas
                            items={clientes}
                            loading={loading}
                            keyExtractor={(c) => c.cuil}
                            textoVacio="No se encontraron clientes con los filtros aplicados."
                            renderTarjeta={(c) => (
                                <>
                                    <div className="mob-tarjeta-header">
                                        <div>
                                            <div className="mob-tarjeta-titulo">{`${c.apellido}, ${c.nombre}`}</div>
                                            <div className="mob-tarjeta-subtitulo">{formatearCuil(c.cuil)}</div>
                                        </div>
                                    </div>
                                    {c.referencia && (
                                        <div className="mob-tarjeta-badges">
                                            <span
                                                className="mob-tarjeta-badge"
                                                style={{
                                                    background: (c.colorReferencia || '#6c757d') + '22',
                                                    color: c.colorReferencia || '#6c757d',
                                                }}
                                            >
                                                {c.referencia}
                                            </span>
                                        </div>
                                    )}
                                    {c.tramites?.length > 0 && (
                                        <div className="mob-tarjeta-badges">
                                            <ChipsTramitesCliente tramites={c.tramites} alinear="flex-start" />
                                        </div>
                                    )}
                                    <div className="mob-tarjeta-meta">
                                        <span><i className="bi bi-calendar3" aria-hidden="true" />{formatearFecha(c.fechaIngreso)}</span>
                                    </div>
                                    <div className="mob-tarjeta-acciones">
                                        <button
                                            type="button"
                                            className="mob-tarjeta-accion primaria mob-tocable"
                                            onClick={() => verDetalles(c.cuil)}
                                        >
                                            <i className="bi bi-eye" aria-hidden="true" />
                                            Ver Detalles
                                        </button>
                                        <button
                                            type="button"
                                            className="mob-tarjeta-accion peligro mob-tocable"
                                            aria-label="Dar de baja"
                                            onClick={() => abrirDialogoEliminar(c.cuil)}
                                        >
                                            <i className="bi bi-trash" aria-hidden="true" />
                                        </button>
                                    </div>
                                </>
                            )}
                        />
                        {!loading && totalPages > 0 && (
                            <div className="d-flex justify-content-center py-3">
                                <Pagination
                                    count={totalPages}
                                    page={page + 1}
                                    onChange={(_e, value) => setPage(value - 1)}
                                    color="primary"
                                />
                            </div>
                        )}
                    </>
                ) : loading ? (
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }}></div>
                        <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando clientes...</p>
                    </div>
                ) : (
                    <div className="card shadow-sm" style={{ border: 'none', borderRadius: '12px' }}>
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead style={{ background: 'var(--gradient-primary-light)', borderBottom: '2px solid #3B82F6' }}>
                                    <tr>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>
                                            <i className="bi bi-card-text me-2"></i>CUIL
                                        </th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>
                                            <i className="bi bi-person me-2"></i>Nombre Completo
                                        </th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>
                                            <i className="bi bi-folder2-open me-2"></i>Trámites
                                        </th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>
                                            <i className="bi bi-calendar-event me-2"></i>Fecha Ingreso
                                        </th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>
                                            <i className="bi bi-bookmark me-2"></i>Referencia
                                        </th>
                                        <th className="text-center" style={{ padding: '1rem', fontWeight: '600', color: '#1e40af' }}>
                                            <i className="bi bi-gear me-2"></i>Acciones
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                {clientes.map((c) => (
                                    <tr 
                                        key={c.cuil}
                                        style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#f8fafc';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                    >
                                        <td className="text-center" style={{ padding: '1rem', fontWeight: '500' }}>
                                            <span className="badge" style={{ background: '#eff6ff', color: '#1e40af', padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.9rem' }}>
                                                {formatearCuil(c.cuil)}
                                            </span>
                                        </td>
                                        <td className="text-center" style={{ padding: '1rem', fontWeight: '500', color: '#374151' }}>
                                            {`${c.apellido}, ${c.nombre}`}
                                        </td>
                                        <td className="text-center" style={{ padding: '1rem', maxWidth: 320 }}>
                                            <ChipsTramitesCliente tramites={c.tramites} />
                                        </td>
                                        <td className="text-center" style={{ padding: '1rem', color: '#6B7280' }}>
                                            <i className="bi bi-calendar3 me-2"></i>{formatearFecha(c.fechaIngreso)}
                                        </td>
                                        <td className="text-center" style={{ padding: '1rem' }}>
                                            {c.referencia ? (
                                                <span
                                                    className="badge"
                                                    style={{
                                                        background: (c.colorReferencia || '#6c757d') + '22',
                                                        color: c.colorReferencia || '#6c757d',
                                                        border: `1px solid ${c.colorReferencia || '#6c757d'}`,
                                                        padding: '0.5rem 0.9rem',
                                                        borderRadius: '6px',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {c.referencia}
                                                </span>
                                            ) : (
                                                <span className="text-muted">—</span>
                                            )}
                                        </td>
                                        <td className="text-center" style={{ padding: '1rem', whiteSpace: 'nowrap', width: '1%' }}>
                                            <button
                                                className="btn btn-primary btn-sm me-2"
                                                onClick={() => verDetalles(c.cuil)}
                                                style={{ borderRadius: '6px', fontWeight: '500', padding: '0.4rem 1rem' }}
                                            >
                                                <i className="bi bi-eye me-1"></i>Ver Detalles
                                            </button>
                                            <button
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => abrirDialogoEliminar(c.cuil)}
                                                style={{ borderRadius: '6px', fontWeight: '500', padding: '0.4rem 0.8rem' }}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {clientes.length === 0 && (
                            <div className="text-center py-5">
                                <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#cbd5e1' }}></i>
                                <p className="mt-3" style={{ color: '#64748b', fontSize: '1.1rem' }}>No se encontraron clientes con los filtros aplicados.</p>
                            </div>
                        )}

                        {totalPages > 0 && (
                            <div className="d-flex justify-content-center py-3 border-top">
                                <Pagination
                                    count={totalPages}
                                    page={page + 1}
                                    onChange={(_e, value) => setPage(value - 1)}
                                    color="primary"
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Quien busca a alguien ve también si pasó por el estudio aunque no sea cliente
                    del sistema: CARPETAS ELIMINADAS y ARCHIVO de la planilla (V253). */}
                <CoincidenciasPlanilla texto={filtroNombreApellido || filtroCuil} />
            </div>

            {/* Modal Registrar */}
            <ModalRegistrarCliente
                open={openModal}
                onClose={handleCloseAndReloadModal}
                onSuccess={handleSuccess}
                mostrarDialogoConfirmacion={true}
            />

            {/* Diálogo de confirmación modernizado */}
            <Dialog open={dialogoEliminar.open} onClose={cerrarDialogoEliminar} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ 
                    background: 'var(--gradient-danger)', 
                    color: 'white', 
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    Confirmar Eliminación
                </DialogTitle>
                <DialogContent dividers sx={{ backgroundColor: '#ffffff', padding: 3 }}>
                    {dialogoEliminar.cuil && (() => {
                        const cliente = clientes.find(c => c.cuil === dialogoEliminar.cuil);
                        if (!cliente) return null;
                        return (
                            <div className="d-flex flex-column gap-3">
                                <div className="text-center mb-3">
                                    <i className="bi bi-person-x-fill" style={{ fontSize: '3rem', color: '#ef4444' }}></i>
                                    <p className="mt-2 mb-0" style={{ fontSize: '1.1rem', color: '#374151' }}>
                                        ¿Deseas dar de baja al siguiente cliente?
                                    </p>
                                </div>
                                
                                <div 
                                    className="p-3 rounded-3" 
                                    style={{ 
                                        background: 'var(--danger-bg)',
                                        border: '1px solid #fecaca'
                                    }}
                                >
                                    <div className="d-flex align-items-center mb-2">
                                        <i className="bi bi-card-text me-2" style={{ color: '#ef4444' }}></i>
                                        <strong style={{ color: '#991b1b' }}>CUIL:</strong>
                                        <span className="ms-2" style={{ color: '#374151' }}>{formatearCuil(cliente.cuil)}</span>
                                    </div>
                                    <div className="d-flex align-items-center mb-2">
                                        <i className="bi bi-person me-2" style={{ color: '#ef4444' }}></i>
                                        <strong style={{ color: '#991b1b' }}>Nombre:</strong>
                                        <span className="ms-2" style={{ color: '#374151' }}>{apellidoNombre(cliente.apellido, cliente.nombre)}</span>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-calendar3 me-2" style={{ color: '#ef4444' }}></i>
                                        <strong style={{ color: '#991b1b' }}>Fecha Ingreso:</strong>
                                        <span className="ms-2" style={{ color: '#374151' }}>{formatearFecha(cliente.fechaIngreso)}</span>
                                    </div>
                                </div>

                                <PreviewCancelacionBajaCliente cuil={cliente.cuil} onCargado={() => setPreviewBajaCargado(true)} />

                                <div className="mt-2">
                                    <label htmlFor="observacionEliminar" className="form-label" >
                                        <i className="bi bi-pencil-square me-2"></i>Observación para la baja:
                                    </label>
                                    <textarea
                                        id="observacionEliminar"
                                        className="form-control"
                                        rows="4"
                                        value={observacionEliminar}
                                        onChange={(e) => setObservacionEliminar(e.target.value)}
                                        placeholder="Ingrese el motivo de la baja..."
                                        style={{ resize: 'none' }}
                                    />
                                </div>
                            </div>
                        );
                    })()}
                </DialogContent>
                <DialogActions sx={{ padding: 2, justifyContent: 'space-between', borderTop: 'none' }}>
                    <button 
                        className="btn btn-outline-secondary" 
                        onClick={cerrarDialogoEliminar}
                        style={{ borderRadius: '8px', padding: '0.6rem 1.5rem', fontWeight: '500' }}
                    >
                        <i className="bi bi-x-circle me-2"></i>Cancelar
                    </button>
                    <button
                        className="btn btn-danger"
                        onClick={confirmarEliminacion}
                        disabled={!previewBajaCargado}
                        style={{ borderRadius: '8px', padding: '0.6rem 1.5rem', fontWeight: '500' }}
                    >
                        <i className="bi bi-trash me-2"></i>Confirmar Baja
                    </button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );

}

export default ListadoClientes;
